/**
 * The search state behind a picker: what was typed, debounced, run through
 * the caller's search, and the options and loading flag that come back.
 *
 * The one implementation every picker's search uses — `useSubjectSearch` is
 * this with `{ subjects }` unwrapped and `searchEmpty: false`. Takes the search
 * as an argument, so it knows no endpoint.
 *
 * **Whether an empty term searches is the caller's choice.** A short list to
 * pick from before anything is typed is how a "person" field is used — there
 * are tens of them; a directory of every user is not offered that way.
 */
import { useCallback, useState } from 'react';
import { useAsync } from './useAsync';
import { useDebounced } from './useDebounced';

export interface RecordSearchOptions {
  /** False while nothing shows the picker — a closed dialog. Defaults to true. */
  enabled?: boolean;
  /** Run the search with an empty term too. Defaults to true. */
  searchEmpty?: boolean;
}

export interface RecordSearch<T> {
  /** What the search returned for the current term. */
  options: readonly T[];
  loading: boolean;
  /** Hand to the picker's `onQueryChange`. Debounced here, not by the picker. */
  setQuery: (query: string) => void;
  /** Forget the term, e.g. when the dialog around the picker closes. */
  reset: () => void;
}

export function useRecordSearch<T>(
  search: (query: string) => Promise<readonly T[]>,
  { enabled = true, searchEmpty = true }: RecordSearchOptions = {},
): RecordSearch<T> {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim());

  const active = enabled && (searchEmpty || debounced.length > 0);
  // `search` is not a dependency: it is a new function every render. See useAsync.
  const found = useAsync(() => search(debounced), [debounced], { enabled: active });

  // Stable, like `setQuery`: a caller may reset from an effect that depends on it.
  const reset = useCallback(() => setQuery(''), []);

  return {
    options: active ? (found.data ?? []) : [],
    loading: active && found.loading,
    setQuery,
    reset,
  };
}
